import { useState, useEffect, useCallback } from 'react'
import {
  type AudienceField,
  type AudienceOp,
  type AudienceFilter,
  type AudienceCond,
  type AudienceAnd,
} from '../../services/adminApi'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// The UI works with a flat list of conditions combined with AND/OR logic.
// We represent the top-level as either an And-node (if multiple conditions) or
// a single Cond-node.

type LogicOp = 'and' | 'or'

interface CondRow {
  id: string
  field: AudienceField
  op: AudienceOp
  value: string
}

interface FilterBuilderProps {
  onChange: (filter: AudienceFilter | null) => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELD_OPTIONS: { value: AudienceField; label: string }[] = [
  { value: 'signup_date', label: 'Signup Date' },
  { value: 'last_seen', label: 'Last Seen' },
  { value: 'shop_tier', label: 'Shop Tier' },
  { value: 'is_seller', label: 'Is Seller' },
  { value: 'country', label: 'Country' },
  { value: 'has_purchased', label: 'Has Purchased' },
  { value: 'email_verified', label: 'Email Verified' },
]

const OP_OPTIONS: { value: AudienceOp; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'in', label: 'in (comma-sep)' },
  { value: 'nin', label: 'not in (comma-sep)' },
  { value: 'contains', label: 'contains' },
]

let _nextId = 1
function nextId(): string {
  return String(_nextId++)
}

function makeEmptyRow(): CondRow {
  return { id: nextId(), field: 'signup_date', op: 'gte', value: '' }
}

// Convert a CondRow to AudienceCond, coercing value to the right type.
function rowToCond(row: CondRow): AudienceCond {
  let value: AudienceCond['value'] = row.value

  if (row.op === 'in' || row.op === 'nin') {
    value = row.value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  } else if (
    row.field === 'is_seller' ||
    row.field === 'has_purchased' ||
    row.field === 'email_verified'
  ) {
    value = row.value.toLowerCase() === 'true'
  } else if (!isNaN(Number(row.value)) && row.value !== '') {
    // Keep as string for date-like values; coerce only clear integers
    const n = Number(row.value)
    if (Number.isInteger(n)) value = n
  }

  return { kind: 'cond', field: row.field, op: row.op, value }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FilterBuilder({ onChange }: FilterBuilderProps) {
  const [rows, setRows] = useState<CondRow[]>([makeEmptyRow()])
  const [logicOp, setLogicOp] = useState<LogicOp>('and')

  const buildFilter = useCallback((): AudienceFilter | null => {
    const validRows = rows.filter((r) => r.value.trim() !== '')
    if (validRows.length === 0) return null

    const conds = validRows.map(rowToCond)
    if (conds.length === 1) return conds[0]

    if (logicOp === 'and') {
      const node: AudienceAnd = { kind: 'and', children: conds }
      return node
    } else {
      return { kind: 'or', children: conds }
    }
  }, [rows, logicOp])

  useEffect(() => {
    onChange(buildFilter())
  }, [buildFilter, onChange])

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()])

  const removeRow = (id: string) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev))

  const updateRow = (id: string, patch: Partial<CondRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))

  const inputStyles =
    'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]/20'
  const selectStyles = `${inputStyles} pr-2`

  return (
    <div className="space-y-3">
      {/* Logic op toggle — only relevant when multiple rows */}
      {rows.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">Match</span>
          <select
            value={logicOp}
            onChange={(e) => setLogicOp(e.target.value as LogicOp)}
            className={selectStyles}
          >
            <option value="and">ALL conditions (AND)</option>
            <option value="or">ANY condition (OR)</option>
          </select>
        </div>
      )}

      {rows.map((row, idx) => (
        <div key={row.id} className="flex items-center gap-2">
          {/* Connector label */}
          {idx > 0 && (
            <span className="text-xs font-medium text-gray-400 w-8 text-center uppercase">
              {logicOp}
            </span>
          )}
          {idx === 0 && <span className="w-8" />}

          <select
            value={row.field}
            onChange={(e) => updateRow(row.id, { field: e.target.value as AudienceField })}
            className={`${selectStyles} flex-1`}
          >
            {FIELD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={row.op}
            onChange={(e) => updateRow(row.id, { op: e.target.value as AudienceOp })}
            className={`${selectStyles} w-36`}
          >
            {OP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <input
            type="text"
            value={row.value}
            onChange={(e) => updateRow(row.id, { value: e.target.value })}
            placeholder={
              row.field === 'signup_date' || row.field === 'last_seen'
                ? 'YYYY-MM-DD'
                : row.field === 'is_seller' || row.field === 'has_purchased' || row.field === 'email_verified'
                ? 'true / false'
                : row.op === 'in' || row.op === 'nin'
                ? 'val1, val2'
                : 'value'
            }
            className={`${inputStyles} flex-1`}
          />

          <button
            type="button"
            onClick={() => removeRow(row.id)}
            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded"
            title="Remove condition"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="text-xs text-[#503864] dark:text-[#9f7aba] hover:underline font-medium cursor-pointer px-2 py-1 rounded hover:bg-[#503864]/10 active:bg-[#503864]/20 transition-colors"
      >
        + Add condition
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Leave a value empty to ignore that condition. Only users with email consent are included.
      </p>
    </div>
  )
}
